import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import detectBrowserLanguage from "detect-browser-language";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import "./App.css";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import FarmProductPage from "./views/farm/products/FarmProductPage";
import FarmProcessedProductPage from "./views/farm/products/FarmProcessedProductPage";
import { FarmFreshProductsPage, FarmProcessedProductsPage } from "./views/farm/products/FarmProductCollectionPage";
import FarmOuter from "./views/farm/FarmOuter";
import Seo from "./components/misc/Seo";
import { setLanguage } from "./components/redux/appSlice";

const textData = {
  gdpr: [
    "Denna webbplats använder cookies för en förbättrad användarupplevelse",
    "This website is using cookies for improved user experience",
  ],
};

const seoData = {
  "/": {
    title: ["VERT.farm | Lokalt odlade mikrogrönt", "VERT.farm | Fresh local microgreens"],
    description: [
      "VERT.farm odlar lokala, färska och näringsrika mikrogrönt för restauranger och hem.",
      "VERT.farm grows local, fresh and nutrient-packed microgreens for restaurants and homes.",
    ],
  },
  "/farm": {
    title: ["VERT.farm | Om oss", "VERT.farm | About us"],
    description: [
      "Lär känna VERT.farm, vår odlingsmetod och varför mikrogrönt gör skillnad.",
      "Discover VERT.farm, our cultivation method and why microgreens make a difference.",
    ],
  },
  "/farm/info": {
    title: ["VERT.farm | Information", "VERT.farm | Information"],
    description: [
      "Information om VERT.farm och hållbar, lokal produktion av mikrogrönt.",
      "Information about VERT.farm and sustainable local microgreen production.",
    ],
  },
  "/farm/products": {
    title: ["VERT.farm | Produkter", "VERT.farm | Products"],
    description: [
      "Utforska VERT.farms färska och processade produkter.",
      "Explore VERT.farm's fresh and processed products.",
    ],
  },
  "/farm/products/fresh": {
    title: ["Färska produkter | VERT.farm", "Fresh products | VERT.farm"],
    description: [
      "Utforska VERT.farms färska mikrogrönsaker.",
      "Explore all fresh microgreens from VERT.farm.",
    ],
  },
  "/farm/products/processed": {
    title: ["Processade produkter | VERT.farm", "Processed products | VERT.farm"],
    description: [
      "Utforska VERT.farms frystorkade och torkade produkter.",
      "Explore VERT.farm's freeze-dried and dehydrated products.",
    ],
  },
  "/farm/partners": {
    title: ["VERT.farm | Partners", "VERT.farm | Partners"],
    description: [
      "Se våra samarbeten och partners inom hållbar och lokal livsmedelsproduktion.",
      "See our collaborations and partners in sustainable local food production.",
    ],
  },
  "/farm/contact": {
    title: ["VERT.farm | Kontakt", "VERT.farm | Contact"],
    description: [
      "Kontakta VERT.farm för beställning, samarbeten eller frågor om mikrogrönt.",
      "Contact VERT.farm for orders, collaborations, or questions about microgreens.",
    ],
  },
};

function RouteSeo({ userLanguage }) {
  const location = useLocation();

  if (location.pathname.startsWith("/farm/product/") || location.pathname.startsWith("/farm/products/processed/")) {
    return null;
  }

  const activeSeo = seoData[location.pathname] || seoData["/"];

  return (
    <Seo
      title={activeSeo.title[userLanguage]}
      description={activeSeo.description[userLanguage]}
      path={location.pathname}
      lang={userLanguage === 0 ? "sv" : "en"}
    />
  );
}

function App() {
  const [gdpr, setGdpr] = useState(false);

  const userLanguage = useSelector((state) => state.app.language);
  const dispatch = useDispatch();

  const handleGDPRAccept = () => {
    localStorage.setItem("gdpr", "true");
    setGdpr(true);
  };

  useEffect(() => {
    if (localStorage.getItem("gdpr")) {
      setGdpr(true);
    }
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("languageIndex");
    if (savedLanguage) {
      dispatch(setLanguage(JSON.parse(savedLanguage)));
      return;
    }
    
    const browserLang = detectBrowserLanguage();
    dispatch(setLanguage(browserLang === "sv-SE" ? 0 : 1));
  }, [dispatch]);

  return (
    <BrowserRouter>
      <RouteSeo userLanguage={userLanguage} />
      <div className="App">
        <div className="AppStart">
          <Routes>
            <Route path="/" element={<FarmOuter />} />
            <Route path="farm" element={<FarmOuter />} />
            <Route path="farm/info" element={<FarmOuter />} />
            <Route path="farm/products" element={<FarmOuter />} />
            <Route path="farm/products/fresh" element={<FarmFreshProductsPage />} />
            <Route path="farm/products/processed" element={<FarmProcessedProductsPage />} />
            <Route path="farm/products/processed/:productId/:batchDate?" element={<FarmProcessedProductPage />} />
            <Route
              path="farm/product/:productId/:batchDate?"
              element={<FarmProductPage />}
            />
            <Route path="farm/partners" element={<FarmOuter />} />
            <Route path="farm/contact" element={<FarmOuter />} />
          </Routes>
        </div>
      </div>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={!gdpr}
        onClose={handleGDPRAccept}
        message={textData.gdpr[userLanguage]}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleGDPRAccept}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </BrowserRouter>
  );
}

export default App;
